import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import TerminalKiosk from './components/TerminalKiosk';
import TerminalManagement from './components/TerminalManagement';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// ================================================================
// SESIÓN OPERATIVA
// ================================================================
// Regla acordada:
// 1) Mientras existe una jornada abierta (Entrada sin Salida), la sesión
//    puede mantenerse viva mediante renovación controlada del JWT.
// 2) Cuando existe una Salida válida, la sesión tiene un máximo de 1 hora
//    contado desde esa Salida, aunque el navegador permanezca abierto.
// 3) Si el equipo/celular se apaga, duerme o pierde la ejecución de la página,
//    se conserva una marca de presencia local. Al regresar, si pasó más de
//    1 hora desde la última presencia registrada, se cierra la sesión.
// 4) La presencia se actualiza mientras la aplicación está ejecutándose, por
//    lo que un apagado real no puede "simular" actividad durante ese periodo.
const SESSION_AFTER_EXIT_MS = 60 * 60 * 1000;
const PRESENCE_GRACE_MS = 60 * 60 * 1000;
const PRESENCE_INTERVAL_MS = 30 * 1000;
const JOURNEY_POLL_INTERVAL_MS = 30 * 1000;
const SESSION_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

const PRESENCE_KEY = 'relojChecador.session.lastPresenceAt';
const SESSION_STARTED_AT_KEY = 'relojChecador.session.startedAt';

const limpiarSesionLocal = () => {
  localStorage.removeItem('token');
  localStorage.removeItem(PRESENCE_KEY);
  localStorage.removeItem(SESSION_STARTED_AT_KEY);
};

const obtenerMarcaTiempoSalida = (jornadaStatus) => {
  const timestamp = jornadaStatus?.lastAttendance?.timestamp;
  if (!timestamp) return null;

  const fecha = new Date(timestamp);
  return Number.isNaN(fecha.getTime()) ? null : fecha.getTime();
};

const tokenExpirado = (token) => {
  try {
    const payload = jwtDecode(token);
    if (!payload?.exp) return false;
    return (Number(payload.exp) * 1000) <= Date.now();
  } catch (_) {
    return true;
  }
};

const obtenerInicioSesionActual = (token) => {
  const almacenado = Number(localStorage.getItem(SESSION_STARTED_AT_KEY) || 0);
  if (almacenado > 0) return almacenado;

  // Compatibilidad con sesiones que ya estaban abiertas antes de instalar
  // esta corrección. El iat del JWT representa el inicio/renovación conocida
  // por el servidor y evita usar una Salida histórica como origen del plazo.
  try {
    const payload = jwtDecode(token);
    const emitidoAt = Number(payload?.iat || 0) * 1000;
    if (emitidoAt > 0) {
      localStorage.setItem(SESSION_STARTED_AT_KEY, String(emitidoAt));
      return emitidoAt;
    }
  } catch (_) {}

  const ahora = Date.now();
  localStorage.setItem(SESSION_STARTED_AT_KEY, String(ahora));
  return ahora;
};

const SessionGuard = ({ token, onTokenChanged, onLogout, children }) => {
  const [sessionReady, setSessionReady] = useState(false);
  const laborandoRef = useRef(false);
  const jornadaStatusRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const logoutTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const cerrarSesion = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    limpiarSesionLocal();
    laborandoRef.current = false;
    jornadaStatusRef.current = null;
    onLogout();
  }, [onLogout]);

  const programarCierrePorSalida = useCallback((jornadaStatus) => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    const salidaAt = obtenerMarcaTiempoSalida(jornadaStatus);
    if (!salidaAt) return false;

    // Si la Salida pertenece a una jornada histórica (por ejemplo, una
    // Salida materializada al aceptar un cierre administrativo), no debe
    // expulsar inmediatamente a quien acaba de iniciar sesión.
    //
    // - Sesión ya activa cuando ocurre la Salida: manda salidaAt.
    // - Inicio de sesión posterior a una Salida histórica: manda el inicio de
    //   la sesión actual y dispone de hasta una hora de sesión autenticada.
    const inicioSesionAt = obtenerInicioSesionActual(token);
    const inicioPlazoAt = Math.max(salidaAt, inicioSesionAt);
    const restante = (inicioPlazoAt + SESSION_AFTER_EXIT_MS) - Date.now();

    if (restante <= 0) {
      cerrarSesion();
      return true;
    }

    logoutTimerRef.current = setTimeout(() => {
      cerrarSesion();
    }, restante);

    return false;
  }, [cerrarSesion, token]);

  const consultarEstadoJornada = useCallback(async (silencioso = true) => {
    if (!token || tokenExpirado(token)) {
      cerrarSesion();
      return null;
    }

    try {
      const response = await axios.get('/api/attendance/jornada-status', {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false,
        cache: 'no-store'
      });

      if (!mountedRef.current) return response.data;

      const estado = response.data || {};
      jornadaStatusRef.current = estado;
      laborandoRef.current = String(estado.state || '').toUpperCase() === 'ABIERTA' && estado.nextAllowedType === 'Salida';

      const cerrada = String(estado.state || '').toUpperCase() === 'CERRADA';
      if (cerrada) {
        programarCierrePorSalida(estado);
      } else if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }

      return estado;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        cerrarSesion();
        return null;
      }

      if (!silencioso) {
        console.error('Error consultando el estado de la jornada para la sesión:', error);
      }

      return null;
    }
  }, [token, cerrarSesion, programarCierrePorSalida]);

  const renovarToken = useCallback(async () => {
    if (!token || refreshInFlightRef.current || !laborandoRef.current) return;

    refreshInFlightRef.current = true;

    try {
      const response = await axios.post('/api/auth/refresh-session', {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });

      const nuevoToken = response.data?.token;
      if (!nuevoToken) throw new Error('El servidor no devolvió un token renovado.');

      localStorage.setItem('token', nuevoToken);
      onTokenChanged(nuevoToken);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        cerrarSesion();
      } else {
        console.warn('No fue posible renovar la sesión mientras la jornada está abierta:', error);
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [token, onTokenChanged, cerrarSesion]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, []);

  // Al cargar/reanudar la aplicación comprobamos cuánto tiempo ha pasado
  // desde la última presencia real del navegador. Esto cubre apagado,
  // suspensión, cierre del navegador y pérdida de ejecución de la página.
  useEffect(() => {
    if (!token) {
      setSessionReady(true);
      return undefined;
    }

    const ahora = Date.now();
    const presenciaAnterior = Number(localStorage.getItem(PRESENCE_KEY) || 0);

    if (tokenExpirado(token)) {
      cerrarSesion();
      return undefined;
    }

    if (presenciaAnterior > 0 && (ahora - presenciaAnterior) > PRESENCE_GRACE_MS) {
      cerrarSesion();
      return undefined;
    }

    localStorage.setItem(PRESENCE_KEY, String(ahora));

    let cancelado = false;

    const iniciar = async () => {
      await consultarEstadoJornada(false);
      if (!cancelado && mountedRef.current) setSessionReady(true);
    };

    iniciar();

    return () => {
      cancelado = true;
    };
  }, [token, cerrarSesion, consultarEstadoJornada]);

  // Presencia: se actualiza mientras la página sigue ejecutándose. Un apagado
  // real no puede actualizar este valor, de modo que al regresar se puede
  // detectar el tiempo transcurrido.
  useEffect(() => {
    if (!token || !sessionReady) return undefined;

    const actualizarPresencia = () => {
      localStorage.setItem(PRESENCE_KEY, String(Date.now()));
    };

    actualizarPresencia();
    const intervalo = setInterval(actualizarPresencia, PRESENCE_INTERVAL_MS);

    const alVolver = () => {
      const ultimo = Number(localStorage.getItem(PRESENCE_KEY) || 0);
      if (ultimo > 0 && (Date.now() - ultimo) > PRESENCE_GRACE_MS) {
        cerrarSesion();
        return;
      }
      actualizarPresencia();
      consultarEstadoJornada(true);
    };

    window.addEventListener('pageshow', alVolver);
    window.addEventListener('focus', alVolver);
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      clearInterval(intervalo);
      window.removeEventListener('pageshow', alVolver);
      window.removeEventListener('focus', alVolver);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [token, sessionReady, cerrarSesion, consultarEstadoJornada]);

  // El estado de jornada es la fuente de verdad para saber si el usuario
  // sigue laborando o ya registró salida. Se consulta con poca frecuencia
  // para no cargar innecesariamente al backend.
  useEffect(() => {
    if (!token || !sessionReady) return undefined;

    consultarEstadoJornada(true);
    const intervalo = setInterval(() => consultarEstadoJornada(true), JOURNEY_POLL_INTERVAL_MS);

    return () => clearInterval(intervalo);
  }, [token, sessionReady, consultarEstadoJornada]);

  // Mientras hay una Entrada abierta, el JWT se renueva antes de vencer.
  // Después de una Salida ya no se renueva: la sesión queda limitada a una
  // hora desde la salida.
  useEffect(() => {
    if (!token || !sessionReady) return undefined;

    const intervalo = setInterval(() => renovarToken(), SESSION_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalo);
  }, [token, sessionReady, renovarToken]);

  if (!token || !sessionReady) {
    return null;
  }

  return children;
};

function AppRoutes({ token, onLogin, onTokenChanged, onLogout }) {
  const location = useLocation();
  const isTerminalRoute = location.pathname === '/terminal';

  // La terminal biométrica independiente no usa la sesión JWT del panel.
  if (isTerminalRoute) {
    return <Routes><Route path="/terminal" element={<TerminalKiosk />} /></Routes>;
  }

  if (!token) {
    return (
      <Routes>
        <Route path="/terminal-admin" element={<LoginForm onLogin={onLogin} />} />
        <Route path="*" element={<LoginForm onLogin={onLogin} />} />
      </Routes>
    );
  }

  return (
    <SessionGuard
      token={token}
      onTokenChanged={onTokenChanged}
      onLogout={onLogout}
    >
      <Routes>
        <Route path="/terminal-admin" element={<TerminalManagement />} />
        <Route path="/" element={<Dashboard token={token} />} />
        <Route path="*" element={<Dashboard token={token} />} />
      </Routes>
    </SessionGuard>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));

  const handleLogin = useCallback((nuevoToken) => {
    const ahora = Date.now();
    localStorage.setItem('token', nuevoToken);
    localStorage.setItem(PRESENCE_KEY, String(ahora));
    // Marca explícitamente el comienzo de ESTA autenticación. Es distinta de
    // la hora de una Salida histórica y no cambia con las renovaciones del JWT.
    localStorage.setItem(SESSION_STARTED_AT_KEY, String(ahora));
    setToken(nuevoToken);
  }, []);

  const handleTokenChanged = useCallback((nuevoToken) => {
    setToken(nuevoToken);
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
  }, []);

  return (
    <Router>
      <AppRoutes
        token={token}
        onLogin={handleLogin}
        onTokenChanged={handleTokenChanged}
        onLogout={handleLogout}
      />
    </Router>
  );
}

export default App;
